import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { collection, getDocs } from 'firebase/firestore';
import db from '../firebaseConfig';
import { COUNTRY_CONFIG, getCollectionName, getCountryLabel } from '../utils/countryConfig';
import { findProductsByCode, saveProductPatch } from '../utils/productPersistence';
import {
  EFFECTS_IMPORT_LIMITS,
  EFFECTS_ROW_LABELS,
  applyEffectsPreview,
  buildEffectsPreview,
  inspectEffectsSheet,
  productEffectsKeys,
  readEffectsSheet,
  summarizeEffectsPreview,
} from '../utils/effectsImport';

const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 };
const cellStyle = { padding: 10, borderBottom: '1px solid #e2e8f0', verticalAlign: 'top', textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
let nextImportId = 0;

export default function EffectsImport({ country, onClose, onApplied }) {
  const [workbook, setWorkbook] = useState(null);
  const [workbookData, setWorkbookData] = useState(null);
  const [filename, setFilename] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);
  const [keyField, setKeyField] = useState('itemNumber');
  const [keyColumn, setKeyColumn] = useState('');
  const [effectsColumn, setEffectsColumn] = useState('');
  const [existingPolicy, setExistingPolicy] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewCountry, setPreviewCountry] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [confirmNumeric, setConfirmNumeric] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const panelRef = useRef(null);
  const mounted = useRef(true);
  const operation = useRef(0);
  const dialogId = useRef(null);
  if (!dialogId.current) dialogId.current = `effects-import-${++nextImportId}`;

  useEffect(() => {
    mounted.current = true;
    const previousFocus = document.activeElement;
    panelRef.current?.focus();
    return () => { mounted.current = false; operation.current += 1; previousFocus?.focus(); };
  }, []);

  useEffect(() => {
    operation.current += 1;
    setPreview(null);
    setPreviewCountry('');
    setConfirmApply(false);
    setConfirmNumeric(false);
    setNotice('');
    setError('');
  }, [country]);

  // Parse only the selected sheet; other sheets must not multiply the row budget.
  const selectedSheet = useMemo(() => {
    if (!workbookData || !sheetName) return {};
    try {
      return { worksheet: XLSX.read(workbookData, {
        type: 'array', sheets: [sheetName], cellNF: true, cellText: true,
        cellDates: false, cellHTML: false, cellFormula: true,
        sheetRows: EFFECTS_IMPORT_LIMITS.rows + 2,
      }).Sheets[sheetName] };
    } catch (problem) { return { error: `無法讀取工作表：${problem.message}` }; }
  }, [workbookData, sheetName]);
  const sheetInfo = useMemo(() => {
    if (selectedSheet.error) return { columns: [], dataRowCount: 0, error: selectedSheet.error };
    if (!selectedSheet.worksheet) return { columns: [], dataRowCount: 0 };
    try { return inspectEffectsSheet(selectedSheet.worksheet, Number(headerRow)); }
    catch (problem) { return { columns: [], dataRowCount: 0, error: problem.message }; }
  }, [selectedSheet, headerRow]);
  const counts = useMemo(() => summarizeEffectsPreview(preview || []), [preview]);
  const readyCount = counts.ready || 0;
  const numericReady = (preview || []).some((row) => row.status === 'ready' && row.numeric);
  const allDisabled = Boolean(busy);
  const validCountry = Boolean(COUNTRY_CONFIG[country]);

  const invalidatePreview = () => {
    operation.current += 1;
    setPreview(null);
    setPreviewCountry('');
    setConfirmApply(false);
    setConfirmNumeric(false);
    setError('');
    setNotice('');
  };

  const readFile = async (event) => {
    const file = event.target.files?.[0];
    invalidatePreview();
    setWorkbook(null);
    setWorkbookData(null);
    setFilename('');
    setSheetName('');
    setKeyColumn('');
    setEffectsColumn('');
    setHeaderRow(1);
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) { setError('請選擇 .xlsx 活頁簿'); return; }
    if (file.size > EFFECTS_IMPORT_LIMITS.fileBytes) { setError('檔案不可超過 5 MB，請先分批處理'); return; }
    const token = ++operation.current;
    setBusy('file');
    try {
      const data = await file.arrayBuffer();
      const parsed = XLSX.read(data, { type: 'array', bookSheets: true });
      if (!parsed.SheetNames.length) throw new Error('活頁簿沒有可讀取的工作表');
      if (!mounted.current || token !== operation.current) return;
      setWorkbook(parsed);
      setWorkbookData(data);
      setFilename(file.name);
      setSheetName(parsed.SheetNames[0]);
    } catch (problem) {
      if (mounted.current && token === operation.current) setError(`無法讀取檔案：${problem.message}`);
    } finally { if (mounted.current) setBusy(''); }
  };

  const createPreview = async () => {
    invalidatePreview();
    if (!validCountry) { setError('無效的商品分類，未讀取或更新任何商品'); return; }
    if (!existingPolicy) { setError('請先選擇既有功效的處理方式'); return; }
    if (!workbook || !sheetName || keyColumn === '' || effectsColumn === '') {
      setError('請選擇工作表、識別碼欄與功效欄'); return;
    }
    const token = ++operation.current;
    setBusy('preview');
    try {
      const rows = readEffectsSheet({
        worksheet: selectedSheet.worksheet, headerRow: Number(headerRow),
        keyColumn: Number(keyColumn), effectsColumn: Number(effectsColumn),
      });
      if (!rows.length) throw new Error('所選標題列後沒有資料');
      const snapshot = await getDocs(collection(db, getCollectionName(country)));
      const products = snapshot.docs.map((document) => ({ ...document.data(), id: document.id }));
      const result = buildEffectsPreview({ rows, products, keyField, existingPolicy });
      if (!mounted.current || token !== operation.current) return;
      setPreview(result);
      setPreviewCountry(country);
      setNotice(`已預覽 ${result.length} 列，尚未寫入。請檢查每列配對、原有與匯入功效`);
    } catch (problem) {
      if (mounted.current && token === operation.current) setError(problem.message || '無法讀取商品，請稍後再試');
    } finally { if (mounted.current) setBusy(''); }
  };

  const applyPreview = async () => {
    if (busy || !preview || !validCountry || country !== previewCountry || !readyCount
      || !confirmApply || (numericReady && !confirmNumeric)) return;
    setBusy('apply');
    setError('');
    setNotice('正在逐筆儲存，請勿關閉或切換頁面');
    try {
      const results = await applyEffectsPreview({
        rows: preview, country: previewCountry, saveProductPatch,
        lookupProductsByKey: async ({ country: lookupCountry, keyField: lookupField, key }) => {
          const products = await findProductsByCode(lookupCountry, key, { preserveWhitespace: true });
          return products.filter((product) => productEffectsKeys(product, lookupField).includes(key));
        },
        onRowResult: (row, index) => {
          if (mounted.current) setPreview((previous) => previous?.map((item, itemIndex) => itemIndex === index ? row : item));
        },
      });
      const summary = summarizeEffectsPreview(results);
      if (mounted.current) {
        setPreview(results);
        setConfirmApply(false);
        setNotice(`已儲存 ${summary.applied || 0} 筆；變更衝突 ${summary.saveConflict || 0} 筆；儲存失敗 ${summary.failed || 0} 筆。所有結果保留於下表`);
      }
      if (summary.applied && onApplied) {
        try { await onApplied({ country: previewCountry, successCount: summary.applied, rows: results }); }
        catch (problem) { if (mounted.current) setError('功效已儲存，但商品列表重新整理失敗；請重新整理頁面'); }
      }
    } catch (problem) {
      if (mounted.current) setError(problem.message || '匯入中斷；請檢查下方逐列結果後重新預覽');
    } finally { if (mounted.current) setBusy(''); }
  };

  const onDialogKeyDown = (event) => {
    if (event.key === 'Escape' && !busy && onClose) { event.preventDefault(); event.stopPropagation(); onClose(); }
    if (event.key !== 'Tab') return;
    const controls = [...panelRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')];
    if (!controls.length) { event.preventDefault(); return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  return createPortal(
    <div className="prep-dialog" style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(15, 23, 42, 0.55)', padding: 16, overflowY: 'auto' }}>
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId.current}-title`}
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        style={{ maxWidth: 1180, margin: '20px auto', background: '#fff', color: '#172033', borderRadius: 12, padding: 24, boxSizing: 'border-box' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <h2 id={`${dialogId.current}-title`} style={{ marginTop: 0 }}>批次匯入功效 · {getCountryLabel(country)}</h2>
          {onClose && <button type="button" disabled={allDisabled} onClick={onClose} aria-label="關閉功效匯入">關閉</button>}
        </div>
        <p>選擇工作表與欄位，先預覽，再確認匯入。僅原樣儲存您提供的功效文字，不會推測或補寫功效。空白功效不會清除原有內容。</p>
        <p style={{ color: '#475569' }}>限 .xlsx、5 MB、1,000 筆資料、100 欄。識別碼請使用 Excel「文字」儲存格以保留前導零；超過 15 位的數值識別碼會被阻擋。</p>
        {!validCountry && <p role="alert">無效的商品分類，無法匯入</p>}
        <label style={fieldStyle}>
          XLSX 檔案
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={allDisabled || !validCountry} onChange={readFile} />
        </label>
        {workbook && <>
          <p style={{ overflowWrap: 'anywhere' }}>檔案：{filename}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '16px 0' }}>
            <label style={fieldStyle}>工作表
              <select value={sheetName} disabled={allDisabled} onChange={(event) => { invalidatePreview(); setSheetName(event.target.value); setKeyColumn(''); setEffectsColumn(''); setHeaderRow(1); }}>
                {workbook.SheetNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <label style={fieldStyle}>標題列（Excel 列號）
              <input type="number" min="1" max={EFFECTS_IMPORT_LIMITS.rows} value={headerRow} disabled={allDisabled} onChange={(event) => { invalidatePreview(); setHeaderRow(event.target.value); }} />
            </label>
            <label style={fieldStyle}>完全相符配對方式
              <select value={keyField} disabled={allDisabled} onChange={(event) => { invalidatePreview(); setKeyField(event.target.value); }}>
                <option value="itemNumber">料號（itemNumber）</option>
                <option value="barcode">條碼（含原始／組合樣式條碼）</option>
              </select>
            </label>
            <label style={fieldStyle}>識別碼欄
              <select value={keyColumn} disabled={allDisabled} onChange={(event) => { invalidatePreview(); setKeyColumn(event.target.value); }}>
                <option value="">請選擇</option>
                {sheetInfo.columns.map((column) => <option key={column.index} value={column.index}>{column.label}</option>)}
              </select>
            </label>
            <label style={fieldStyle}>功效欄
              <select value={effectsColumn} disabled={allDisabled} onChange={(event) => { invalidatePreview(); setEffectsColumn(event.target.value); }}>
                <option value="">請選擇</option>
                {sheetInfo.columns.map((column) => <option key={column.index} value={column.index}>{column.label}</option>)}
              </select>
            </label>
            <label style={fieldStyle}>已有功效時
              <select value={existingPolicy} disabled={allDisabled} onChange={(event) => { invalidatePreview(); setExistingPolicy(event.target.value); }}>
                <option value="">請明確選擇處理方式</option>
                <option value="skip">略過已有功效的商品</option>
                <option value="overwrite">以匯入文字覆寫既有功效</option>
              </select>
            </label>
          </div>
          <p>會讀取標題列之後的所有列（含空白列、隱藏列）。識別碼保留大小寫與空白，不以商品名稱模糊配對。</p>
          {sheetInfo.error ? <p role="alert">{sheetInfo.error}</p> : <p>待預覽：{sheetInfo.dataRowCount} 列</p>}
          <button type="button" disabled={allDisabled || !validCountry || Boolean(sheetInfo.error) || keyColumn === '' || effectsColumn === '' || !existingPolicy} onClick={createPreview}>
            {busy === 'preview' ? '讀取商品與配對中…' : preview ? '重新讀取商品並預覽' : '產生匯入預覽'}
          </button>
        </>}
        {busy === 'file' && <p role="status">讀取工作表中…</p>}
        {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
        {notice && <p role="status" aria-live="polite">{notice}</p>}
        {preview && <>
          <h3>預覽結果 · {getCountryLabel(previewCountry)}</h3>
          <p>{Object.entries(counts).map(([status, count]) => `${EFFECTS_ROW_LABELS[status]}：${count}`).join('；')}</p>
          <div role="region" aria-label="功效匯入逐列預覽" tabIndex={0} style={{ maxHeight: '50vh', overflow: 'auto', border: '1px solid #cbd5e1', borderRadius: 8 }}>
            <table style={{ width: '100%', minWidth: 800, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ background: '#f1f5f9' }}>
                <th scope="col" style={{ ...cellStyle, width: 45 }}>列</th>
                <th scope="col" style={cellStyle}>識別碼</th>
                <th scope="col" style={cellStyle}>配對商品</th>
                <th scope="col" style={cellStyle}>原有功效</th>
                <th scope="col" style={cellStyle}>匯入功效</th>
                <th scope="col" style={cellStyle}>結果／提醒</th>
              </tr></thead>
              <tbody>{preview.map((row) => <tr key={row.rowNumber}>
                <td style={cellStyle}>{row.rowNumber}</td>
                <td style={cellStyle}>{row.key || '（空白）'}</td>
                <td style={cellStyle}>
                  {row.matches.length ? row.matches.map((product) => <div key={product.id} style={{ marginBottom: 6 }}>
                    <div>{product.itemNumber || '（無料號）'} · {product.name || '（無品名）'}</div>
                    <small>ID：{product.id}</small>
                  </div>) : '無完全相符商品'}
                </td>
                <td style={cellStyle}>{row.existingEffects || '（空白）'}</td>
                <td style={cellStyle}>{row.effects || '（空白）'}</td>
                <td style={cellStyle}>
                  <strong>{EFFECTS_ROW_LABELS[row.status]}</strong>
                  {row.message && <div>{row.message}</div>}
                  {row.warning && <div style={{ color: '#92400e', marginTop: 6 }}>{row.warning}</div>}
                </td>
              </tr>)}</tbody>
            </table>
          </div>
          <div style={{ display: 'grid', gap: 12, marginTop: 18 }}>
            {numericReady && <label><input type="checkbox" checked={confirmNumeric} disabled={allDisabled} onChange={(event) => setConfirmNumeric(event.target.checked)} /> 我已對照原始資料，確認數值識別碼沒有遺失前導零或位數</label>}
            {readyCount > 0 && <label><input type="checkbox" checked={confirmApply} disabled={allDisabled} onChange={(event) => setConfirmApply(event.target.checked)} /> 我已核對預覽，確認對「{getCountryLabel(previewCountry)}」匯入 {readyCount} 筆功效（{existingPolicy === 'overwrite' ? '覆寫既有功效' : '略過已有功效'}）</label>}
            <button type="button" disabled={allDisabled || !readyCount || !confirmApply || (numericReady && !confirmNumeric) || previewCountry !== country} onClick={applyPreview}>
              {busy === 'apply' ? '逐筆儲存中…' : `確認匯入 ${readyCount} 筆功效`}
            </button>
            <small>重複衝突與多筆配對不會寫入。每筆儲存前會重新核對識別碼；若配對、料號、條碼或功效在預覽後變更，該筆會回報衝突，請重新預覽。失敗列與成功列都會保留。</small>
          </div>
        </>}
      </section>
    </div>,
    document.body,
  );
}
